import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MonthlyBarChart } from '@kilat/ui';

import { useAuth, useRecruiterId } from '../lib/use-auth.js';
import { useI18n } from '../lib/use-i18n.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { useTrackedSnapshot } from '../lib/use-tracked-snapshot.js';
import {
  recruiterAgentsBackrefRepoFor,
  recruiterEarningsLedgerRepoFor,
  recruitersRepo,
} from '../lib/repositories/recruiters-repo.js';
import {
  computeMilestones,
  earningsStreakMonths,
  filterLedgerByRange,
  forecastNextMonthSen,
  money,
  paidOutSen,
  summariseLedgerByMonth,
  summariseLedgerTotals,
  unpaidBalanceSen,
  type EarningsLedgerEntry,
  type Milestone,
  type NextMonthForecast,
  type RecruiterAgentBackref,
  type RecruiterProfile,
} from '@kilat/shared';

/**
 * /recruiter — dashboard for the recruiter persona inside the
 * agent-pwa shell (Sprint 85).
 *
 * Three live subscriptions: the recruiter's own profile, their downstream
 * agents backref collection, and their earnings ledger. Each renders an
 * explicit state per [feedback-no-dead-ends]:
 *   - signed-out → CTA back to marketing apply page
 *   - not-a-recruiter → friendly redirect copy
 *   - loading → skeleton hero + skeleton stats
 *   - error → message + reload CTA
 *   - empty (no downstream yet) → explainer of what triggers an entry
 *   - populated → hero with code + 4 stat tiles + recent activity feed
 *
 * Math:
 *   - downstreamAgents       = backref docs count
 *   - activeDownstream       = subset where currentAgentStatus = 'active'
 *   - lifetimeEarningsSen    = net earnings, EXCLUDING payout debits
 *   - unpaidBalanceSen       = lifetime earnings MINUS amounts already paid out
 *   - thisMonthEarningsSen   = sum of accrued entries inside this calendar month
 *
 * The ledger is the source of truth for money — the back-ref's
 * `lifetimeRecruiterEarningsSen` is a write-cached convenience that
 * could drift; the dashboard derives totals from the ledger so the
 * numbers always reconcile to entries the recruiter can audit.
 */
export function RecruiterHomePage() {
  const { user, loading: authLoading } = useAuth();
  const recruiterId = useRecruiterId();
  const t = useI18n();
  const brand = usePlatformBrand();
  // Profile-listener failure + retry. The recruiter dashboard is gated on the
  // profile doc; without onError a denied/dropped read left it loading forever,
  // and a missing/unparseable doc (repo emits null) was rendered as a permanent
  // skeleton (the null was conflated with "still loading"). Both are now explicit
  // states with a next action (per [[feedback-no-dead-ends]]).
  // Per-listener error state (NOT one shared flag): the profile, downstream, and
  // ledger listeners each own their error, so a healthy listener's success can't
  // clear another's (the me-loyalty resurrection trap). Critically, a denied or
  // dropped DOWNSTREAM/LEDGER read must surface as an explicit error+retry —
  // never a silent RM 0.00 on the money hero (per [[feedback-no-dead-ends]]).
  // `loadError` is their union for the shared error screen.
  const [profileError, setProfileError] = useState<Error | null>(null);
  const [downstreamError, setDownstreamError] = useState<Error | null>(null);
  const [ledgerError, setLedgerError] = useState<Error | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const loadError = profileError ?? ledgerError ?? downstreamError;
  const retryAll = () => {
    setProfileError(null);
    setDownstreamError(null);
    setLedgerError(null);
    setRetryNonce((n) => n + 1);
  };

  const profileSub = useMemo(() => {
    if (!recruiterId) return null;
    return (cb: (v: RecruiterProfile | null) => void) =>
      recruitersRepo.snapshot(
        recruiterId,
        (v) => {
          setProfileError(null);
          cb(v);
        },
        (err) => setProfileError(err),
      );
  }, [recruiterId]);
  const { data: profile, loading: profileLoading } = useTrackedSnapshot<RecruiterProfile | null>(
    ['recruiter-profile', recruiterId, retryNonce],
    profileSub,
  );

  const agentsSub = useMemo(() => {
    if (!recruiterId) return null;
    const repo = recruiterAgentsBackrefRepoFor(recruiterId);
    return (cb: (vs: RecruiterAgentBackref[]) => void) =>
      repo.listSnapshot(
        (v) => {
          setDownstreamError(null);
          cb(v);
        },
        (err) => setDownstreamError(err),
      );
  }, [recruiterId]);
  const { data: downstream } = useTrackedSnapshot<RecruiterAgentBackref[]>(
    ['recruiter-agents', recruiterId, retryNonce],
    agentsSub,
  );

  const ledgerSub = useMemo(() => {
    if (!recruiterId) return null;
    const repo = recruiterEarningsLedgerRepoFor(recruiterId);
    return (cb: (vs: EarningsLedgerEntry[]) => void) =>
      repo.listSnapshot(
        (v) => {
          setLedgerError(null);
          cb(v);
        },
        (err) => setLedgerError(err),
      );
  }, [recruiterId]);
  const { data: ledger } = useTrackedSnapshot<EarningsLedgerEntry[]>(
    ['recruiter-ledger', recruiterId, retryNonce],
    ledgerSub,
  );

  if (authLoading) {
    return (
      <PageShell>
        <Skeleton />
      </PageShell>
    );
  }
  if (!user) {
    return (
      <PageShell>
        <EmptyState
          title={t.t('agent.payouts.signedOutTitle')}
          body={t.t('agent.recruiterHome.signedOutBody')}
        />
      </PageShell>
    );
  }
  if (!recruiterId) {
    return (
      <PageShell>
        <EmptyState
          title={t.t('agent.payouts.notRecruiterTitle')}
          body={t.t('agent.recruiterHome.notRecruiterBody')}
          ctaLabel={t.t('agent.payouts.notRecruiterCta')}
          ctaHref={`https://${brand.domains.marketing}/become-a-recruiter`}
        />
      </PageShell>
    );
  }
  if (loadError) {
    return (
      <PageShell>
        <EmptyState
          title={t.t('agent.loadError.recruiterProfileTitle')}
          body={t.t('agent.loadError.body')}
          onRetry={retryAll}
          retryLabel={t.t('agent.loadError.retry')}
        />
      </PageShell>
    );
  }
  if (profileLoading) {
    return (
      <PageShell>
        <Skeleton />
      </PageShell>
    );
  }
  if (profile === null) {
    return (
      <PageShell>
        <EmptyState
          title={t.t('agent.profileGone.recruiterTitle')}
          body={t.t('agent.profileGone.body')}
          onRetry={retryAll}
          retryLabel={t.t('agent.profileGone.retry')}
        />
      </PageShell>
    );
  }

  const entries = ledger ?? [];
  const nowMs = Date.now();
  const stats = summariseLedger(entries, nowMs);
  const downstreamList = downstream ?? [];
  const activeDownstream = downstreamList.filter((d) => d.currentAgentStatus === 'active');

  // Sprint 94 motivation pieces — forecast + chart + milestones.
  const buckets = summariseLedgerByMonth(entries, nowMs, 6);
  const forecast = forecastNextMonthSen(buckets, nowMs, 3);
  const streak = earningsStreakMonths(buckets, nowMs);
  const milestones = computeMilestones({
    activeOperatorCount: activeDownstream.length,
    lifetimeOperatorCount: downstreamList.length,
    lifetimeEarningsSen: stats.lifetimeEarningsSen,
    earningsStreakMonths: streak,
  });

  return (
    <PageShell>
      <Hero profile={profile} unpaidSen={stats.unpaidBalanceSen} />

      <StatRow>
        <StatTile
          label={t.t('agent.recruiterHome.statDownstream')}
          value={`${downstreamList.length}`}
          hint={t.t('agent.recruiterHome.statDownstreamHint', { n: activeDownstream.length })}
        />
        <StatTile
          label={t.t('agent.recruiterHome.statEarnedThisMonth')}
          value={money.format(stats.thisMonthEarningsSen)}
          hint={t.t('agent.recruiterHome.statEarnedHint')}
        />
        <StatTile
          label={t.t('agent.recruiterHome.statLifetime')}
          value={money.format(stats.lifetimeEarningsSen)}
          hint={t.t('agent.recruiterHome.statLifetimeHint')}
        />
        <StatTile
          label={t.t('agent.recruiterHome.statPaidOut')}
          value={money.format(stats.paidSen)}
          hint={t.t('agent.recruiterHome.statPaidOutHint')}
        />
      </StatRow>

      <ForecastCard
        forecast={forecast}
        activeDownstream={activeDownstream.length}
        lifetimeDownstream={downstreamList.length}
        streak={streak}
      />

      <section style={cardStyle}>
        <header style={cardHeaderRow}>
          <h2 style={cardTitle}>{t.t('agent.recruiterHome.last6Months')}</h2>
          <span style={cardHint}>{t.t('agent.recruiterHome.perSignupHint')}</span>
        </header>
        <MonthlyBarChart
          buckets={buckets}
          formatMoney={(s) => money.format(s)}
          tone="recruiter"
          emptyLabel={t.t('chart.emptyLabel')}
          emptyBody={t.t('chart.emptyBody')}
        />
      </section>

      <MilestonesStrip milestones={milestones} />

      <QuickActions />

      {downstreamList.length === 0 ? (
        <EmptyState
          title={t.t('agent.recruiterAgents.emptyTitle')}
          body={t.t('agent.recruiterHome.emptyBody')}
        />
      ) : (
        <RecentActivity entries={entries.slice(0, 6)} downstreamCount={downstreamList.length} />
      )}
    </PageShell>
  );
}

// ── Sprint 94 — motivation sub-components (mirror of agent /home) ────

function ForecastCard({
  forecast,
  activeDownstream,
  lifetimeDownstream,
  streak,
}: {
  readonly forecast: NextMonthForecast;
  readonly activeDownstream: number;
  readonly lifetimeDownstream: number;
  readonly streak: number;
}) {
  const t = useI18n();
  const confidenceWording = (() => {
    switch (forecast.confidence) {
      case 'high':
        return t.t('agent.recruiterHome.forecastConfidenceHigh');
      case 'medium':
        return t.t('agent.recruiterHome.forecastConfidenceMedium');
      case 'low':
        return t.t('agent.recruiterHome.forecastConfidenceLow');
      case 'none':
        return t.t('agent.recruiterHome.forecastConfidenceNone');
    }
  })();
  const hasData = forecast.confidence !== 'none';
  return (
    <section
      style={{
        background: 'linear-gradient(135deg, #b45309 0%, #f59e0b 100%)',
        color: '#ffffff',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-6)',
        boxShadow: 'var(--shadow-2)',
        display: 'grid',
        gap: 'var(--space-3)',
      }}
    >
      <p
        style={{
          margin: 0,
          fontSize: 'var(--text-meta)',
          opacity: 0.85,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
        }}
      >
        {t.t('agent.recruiterHome.forecastEyebrow')}
      </p>
      <div
        style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)', flexWrap: 'wrap' }}
      >
        <span
          style={{
            fontSize: 'var(--text-display)',
            fontWeight: 600,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {hasData ? money.format(forecast.recurringSen) : '—'}
        </span>
        <span style={{ fontSize: 'var(--text-meta)', opacity: 0.85 }}>
          {hasData
            ? t.t('agent.recruiterHome.forecastSublineData', {
                signups: forecast.avgSignupsPerMonth.toFixed(1),
              })
            : t.t('agent.recruiterHome.forecastSublineNone')}
        </span>
      </div>
      <p
        style={{
          margin: 0,
          fontSize: 'var(--text-meta)',
          opacity: 0.85,
          lineHeight: 1.5,
        }}
      >
        {confidenceWording}.{' '}
        {hasData
          ? t.t('agent.recruiterHome.forecastBodyData')
          : t.t('agent.recruiterHome.forecastBodyNone')}
      </p>
      <div
        style={{
          display: 'flex',
          gap: 'var(--space-3)',
          flexWrap: 'wrap',
          fontSize: 'var(--text-meta)',
          opacity: 0.92,
        }}
      >
        <span>
          <strong>{lifetimeDownstream}</strong> {t.t('agent.recruiterHome.agentsSigned')}
        </span>
        <span>·</span>
        <span>
          <strong>{activeDownstream}</strong> {t.t('agent.recruiterHome.active')}
        </span>
        {streak > 0 ? <span>·</span> : null}
        {streak > 0 ? (
          <span>
            <strong>{t.t('agent.recruiterHome.nMonth', { n: streak })}</strong>{' '}
            {t.t('agent.recruiterHome.signupStreak')}
          </span>
        ) : null}
      </div>
    </section>
  );
}

function MilestonesStrip({ milestones }: { readonly milestones: readonly Milestone[] }) {
  const t = useI18n();
  const unlocked = milestones.filter((m) => m.unlocked);
  const nextUp = milestones.filter((m) => !m.unlocked).sort((a, b) => b.progress - a.progress);
  const featured = nextUp[0];
  return (
    <section style={cardStyle}>
      <header style={cardHeaderRow}>
        <h2 style={cardTitle}>{t.t('agent.recruiterHome.milestones')}</h2>
        <span style={cardHint}>
          {t.t('agent.recruiterHome.milestonesUnlocked', {
            unlocked: unlocked.length,
            total: milestones.length,
          })}
        </span>
      </header>
      <ul
        style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 'var(--space-2)' }}
      >
        {featured ? <MilestoneRow milestone={featured} highlight /> : null}
        {unlocked.slice(0, 3).map((m) => (
          <MilestoneRow key={m.id} milestone={m} />
        ))}
      </ul>
    </section>
  );
}

function MilestoneRow({
  milestone,
  highlight,
}: {
  readonly milestone: Milestone;
  readonly highlight?: boolean;
}) {
  const t = useI18n();
  return (
    <li
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        gap: 'var(--space-3)',
        alignItems: 'center',
        padding: 'var(--space-3)',
        borderRadius: 'var(--radius-md)',
        background: milestone.unlocked
          ? 'rgba(13,122,79,0.06)'
          : highlight
            ? 'rgba(245,158,11,0.06)'
            : 'var(--tint-subtle)',
        border: milestone.unlocked
          ? '1px solid rgba(13,122,79,0.22)'
          : highlight
            ? '1px solid rgba(245,158,11,0.30)'
            : '1px solid var(--color-border)',
      }}
    >
      <ProgressRing
        fraction={milestone.unlocked ? 1 : milestone.progress}
        unlocked={milestone.unlocked}
      />
      <div>
        <p style={{ margin: 0, fontWeight: 600, fontSize: 'var(--text-meta)' }}>
          {t.t(milestone.titleKey, milestone.vars)}
          {!milestone.unlocked && highlight ? ` · ${t.t('agent.home.nextUp')}` : ''}
        </p>
        <p
          style={{
            margin: 'var(--space-1) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            lineHeight: 1.4,
          }}
        >
          {t.t(milestone.bodyKey, milestone.vars)}
        </p>
      </div>
    </li>
  );
}

function ProgressRing({
  fraction,
  unlocked,
}: {
  readonly fraction: number;
  readonly unlocked: boolean;
}) {
  const r = 16;
  const cx = 20;
  const cy = 20;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - Math.max(0, Math.min(1, fraction)));
  const color = unlocked ? 'var(--color-primary)' : '#f59e0b';
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" aria-hidden focusable="false">
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--color-border)" strokeWidth={3} />
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={3}
        strokeLinecap="round"
        strokeDasharray={`${circ} ${circ}`}
        strokeDashoffset={offset}
        transform={`rotate(-90 ${cx} ${cy})`}
        style={{ transition: 'stroke-dashoffset 0.4s ease' }}
      />
      {unlocked ? (
        <path
          d="M14 20 L18 24 L26 16"
          stroke={color}
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      ) : null}
    </svg>
  );
}

const cardHeaderRow: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  gap: 'var(--space-2)',
  flexWrap: 'wrap',
};

const cardTitle: React.CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-body)',
  fontWeight: 600,
};

const cardHint: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
};

// ── components ─────────────────────────────────────────────────────────

function Hero({
  profile,
  unpaidSen,
}: {
  readonly profile: RecruiterProfile;
  readonly unpaidSen: number;
}) {
  const t = useI18n();
  return (
    <section
      style={{
        background: 'linear-gradient(135deg, #0d7a4f 0%, #16956c 100%)',
        color: '#ffffff',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-6)',
        boxShadow: 'var(--shadow-2)',
        display: 'grid',
        gap: 'var(--space-3)',
      }}
    >
      <p
        style={{
          margin: 0,
          fontSize: 'var(--text-meta)',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          opacity: 0.78,
        }}
      >
        {t.t('agent.recruiterHome.recruiterName', { name: profile.name })}
      </p>
      <div
        style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)', flexWrap: 'wrap' }}
      >
        <span
          style={{
            fontFamily: 'ui-monospace, monospace',
            fontSize: 'var(--text-h2)',
            fontWeight: 600,
            letterSpacing: 2,
          }}
        >
          {profile.code}
        </span>
        <span
          style={{
            padding: '2px 10px',
            borderRadius: 'var(--radius-pill)',
            background: 'rgba(255,255,255,0.18)',
            fontSize: 'var(--text-meta)',
            fontWeight: 500,
            textTransform: 'capitalize',
          }}
        >
          {t.t('agent.recruiterHome.tier', { tier: profile.tier })}
        </span>
      </div>
      <div
        style={{
          marginTop: 'var(--space-3)',
          padding: 'var(--space-3) var(--space-4)',
          borderRadius: 'var(--radius-md)',
          background: 'rgba(0,0,0,0.18)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 'var(--space-3)',
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontSize: 'var(--text-meta)', opacity: 0.85 }}>
          {t.t('agent.recruiterHome.unpaidBalance')}
        </span>
        <strong
          style={{
            fontSize: 'var(--text-h2)',
            fontWeight: 600,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {money.format(unpaidSen)}
        </strong>
      </div>
      <p
        style={{
          margin: 0,
          fontSize: 'var(--text-meta)',
          opacity: 0.78,
          lineHeight: 1.5,
        }}
      >
        {t.t('agent.recruiterHome.payoutSchedule')}
      </p>
    </section>
  );
}

function StatRow({ children }: { readonly children: React.ReactNode }) {
  return (
    <section
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 160px), 1fr))',
        gap: 'var(--space-3)',
      }}
    >
      {children}
    </section>
  );
}

function StatTile({
  label,
  value,
  hint,
}: {
  readonly label: string;
  readonly value: string;
  readonly hint: string;
}) {
  return (
    <div
      style={{
        background: 'var(--color-surface)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-4)',
        boxShadow: 'var(--shadow-1)',
        display: 'grid',
        gap: 'var(--space-1)',
      }}
    >
      <p
        style={{
          margin: 0,
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </p>
      <p
        style={{
          margin: 0,
          fontSize: 'var(--text-h2)',
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </p>
      <p
        style={{ margin: 0, fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}
      >
        {hint}
      </p>
    </div>
  );
}

function QuickActions() {
  const t = useI18n();
  return (
    <section style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
      <Link to="/recruiter/code" style={primaryCta}>
        {t.t('agent.recruiterHome.shareMyCode')}
      </Link>
      <Link to="/recruiter/agents" style={secondaryCta}>
        {t.t('agent.recruiterHome.myAgents')}
      </Link>
      <Link to="/recruiter/earnings" style={secondaryCta}>
        {t.t('agent.recruiterHome.earningsLedger')}
      </Link>
    </section>
  );
}

function RecentActivity({
  entries,
  downstreamCount,
}: {
  readonly entries: readonly EarningsLedgerEntry[];
  readonly downstreamCount: number;
}) {
  const t = useI18n();
  if (entries.length === 0) {
    return (
      <section style={cardStyle}>
        <h2 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>
          {t.t('agent.recruiterHome.recentActivity')}
        </h2>
        <p
          style={{
            margin: 'var(--space-3) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            lineHeight: 1.5,
          }}
        >
          {downstreamCount > 0
            ? t.t('agent.recruiterHome.recentActivityEmptyPending')
            : t.t('agent.recruiterHome.recentActivityEmptyNone')}
        </p>
      </section>
    );
  }
  return (
    <section style={cardStyle}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: 'var(--space-3)',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>
          {t.t('agent.recruiterHome.recentActivity')}
        </h2>
        <Link
          to="/recruiter/earnings"
          style={{
            fontSize: 'var(--text-meta)',
            color: 'var(--color-primary)',
            textDecoration: 'none',
            fontWeight: 500,
          }}
        >
          {t.t('agent.recruiterHome.viewAll')}
        </Link>
      </div>
      <ul
        style={{
          listStyle: 'none',
          margin: 0,
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-2)',
        }}
      >
        {entries
          .slice()
          .sort((a, b) => tsMs(b.accruedAt) - tsMs(a.accruedAt))
          .map((e) => (
            <li key={e.id} style={activityRowStyle}>
              <div>
                <strong style={{ fontSize: 'var(--text-meta)', fontWeight: 500 }}>
                  {kindLabel(e.kind, e.basis)}
                </strong>
                <p
                  style={{
                    margin: 'var(--space-1) 0 0 0',
                    fontSize: 'var(--text-meta)',
                    color: 'var(--color-on-surface-muted)',
                  }}
                >
                  {e.invoiceId ? `Invoice ${e.invoiceId} · ` : ''}
                  {formatDate(tsMs(e.accruedAt))}
                </p>
              </div>
              <strong
                style={{
                  fontVariantNumeric: 'tabular-nums',
                  color: e.amount < 0 ? '#b45309' : 'var(--color-primary)',
                }}
              >
                {e.amount > 0 ? '+' : ''}
                {money.format(e.amount)}
              </strong>
            </li>
          ))}
      </ul>
    </section>
  );
}

// ── shared shell + helpers ─────────────────────────────────────────────

export function PageShell({ children }: { readonly children: React.ReactNode }) {
  return (
    <main
      className="ag-page"
      style={{
        minHeight: '100dvh',
        background: 'var(--tint-subtle)',
        padding: 'var(--space-4) var(--space-4) var(--space-12) var(--space-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-4)',
        margin: '0 auto',
      }}
    >
      {children}
    </main>
  );
}

function Skeleton() {
  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}
      aria-busy="true"
    >
      <div
        style={{ height: 180, borderRadius: 'var(--radius-lg)', background: 'var(--color-border)' }}
      />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 160px), 1fr))',
          gap: 'var(--space-3)',
        }}
      >
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            style={{
              height: 96,
              borderRadius: 'var(--radius-lg)',
              background: 'var(--color-border)',
            }}
          />
        ))}
      </div>
    </div>
  );
}

export function EmptyState({
  title,
  body,
  ctaLabel,
  ctaHref,
  onRetry,
  retryLabel,
}: {
  readonly title: string;
  readonly body: string;
  readonly ctaLabel?: string;
  readonly ctaHref?: string;
  readonly onRetry?: () => void;
  readonly retryLabel?: string;
}) {
  return (
    <section style={{ ...cardStyle, textAlign: 'center' }}>
      <h2 style={{ margin: 0, fontSize: 'var(--text-h2)', fontWeight: 600 }}>{title}</h2>
      <p
        style={{
          margin: 'var(--space-3) auto 0 auto',
          maxWidth: 460,
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          lineHeight: 1.5,
        }}
      >
        {body}
      </p>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          style={{
            ...primaryCta,
            marginTop: 'var(--space-4)',
            minHeight: 'var(--touch-target, 44px)',
            cursor: 'pointer',
          }}
        >
          {retryLabel}
        </button>
      ) : null}
      {ctaLabel && ctaHref ? (
        <a href={ctaHref} style={{ ...primaryCta, marginTop: 'var(--space-4)' }}>
          {ctaLabel}
        </a>
      ) : null}
    </section>
  );
}

const cardStyle: React.CSSProperties = {
  background: 'var(--color-surface)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--space-5)',
  boxShadow: 'var(--shadow-1)',
};

const activityRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--space-3)',
  padding: 'var(--space-3)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--tint-subtle)',
};

const primaryCta: React.CSSProperties = {
  display: 'inline-block',
  padding: 'var(--space-3) var(--space-5)',
  borderRadius: 'var(--radius-pill)',
  background: 'var(--color-primary)',
  color: 'var(--color-primary-fg)',
  textDecoration: 'none',
  fontWeight: 500,
  fontSize: 'var(--text-meta)',
  border: 'none',
};

const secondaryCta: React.CSSProperties = {
  display: 'inline-block',
  padding: 'var(--space-3) var(--space-5)',
  borderRadius: 'var(--radius-pill)',
  background: 'transparent',
  color: 'var(--color-on-surface)',
  textDecoration: 'none',
  fontWeight: 500,
  fontSize: 'var(--text-meta)',
  border: '1px solid var(--color-border-strong)',
};

interface LedgerSummary {
  readonly unpaidBalanceSen: number;
  readonly lifetimeEarningsSen: number;
  readonly thisMonthEarningsSen: number;
  readonly paidSen: number;
}

function summariseLedger(entries: readonly EarningsLedgerEntry[], nowMs: number): LedgerSummary {
  // All four figures come from the shared, tested ledger-summary helpers so
  // the recruiter dashboard reconciles to the same math as the agent /home and
  // both /earnings pages (rule 9 — one source of truth for money).
  //
  //   lifetime  = net earnings, EXCLUDING payout debits (money moved out is
  //               not negative earnings).
  //   unpaid    = lifetime MINUS amounts already paid out. The previous inline
  //               sum summed only the NON-'paid' rows — which equals lifetime —
  //               so it never subtracted disbursed money and OVER-REPORTED the
  //               balance owed by the settled amount (W5 money-accuracy bug).
  //   thisMonth = accrued-only earnings inside the current calendar month.
  //   paid      = total disbursed, as a positive figure.
  const { net } = summariseLedgerTotals(entries);
  const thisMonth = summariseLedgerTotals(
    filterLedgerByRange(entries, 'this_month', nowMs),
  ).accrued;
  return {
    unpaidBalanceSen: unpaidBalanceSen(entries),
    lifetimeEarningsSen: net,
    thisMonthEarningsSen: thisMonth,
    paidSen: paidOutSen(entries),
  };
}

export function kindLabel(
  kind: EarningsLedgerEntry['kind'],
  basis: EarningsLedgerEntry['basis'],
): string {
  const shareKind =
    typeof (basis as Record<string, unknown>)['shareKind'] === 'string'
      ? ((basis as Record<string, unknown>)['shareKind'] as string)
      : null;
  if (kind === 'paid') return 'Paid out';
  if (kind === 'clawed_back') return 'Clawback';
  if (kind === 'adjustment') return 'Adjustment';
  // accrued — show share kind so signup vs first-invoice is obvious
  if (shareKind === 'recruiter_first_invoice') return 'Onboarding share';
  if (shareKind === 'recruiter_signup_commission') return 'Per-signup commission';
  return 'Per-signup commission';
}

export function tsMs(ts: { toMillis?: () => number; seconds?: number } | unknown): number {
  if (!ts || typeof ts !== 'object') return 0;
  const obj = ts as { toMillis?: () => number; seconds?: number; nanoseconds?: number };
  if (typeof obj.toMillis === 'function') return obj.toMillis();
  if (typeof obj.seconds === 'number') {
    return obj.seconds * 1000 + Math.floor((obj.nanoseconds ?? 0) / 1_000_000);
  }
  return 0;
}

export function formatDate(ms: number): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
